/* eslint-disable no-unused-vars */
import { useEffect, useMemo, useState } from "react";
import MainLayout from "../../../layouts/MainLayout";
import { useNavigate, useParams } from "react-router-dom";
import "../../../assets/styles/custom-styles.css";
import useLoader from "../../../hooks/useLoader";
import useForm from "../../../hooks/useForm";
import { detailsMovementFields } from "../../../constants/forms/shipmentFields";
import { setDefaultFieldValues } from "../../../utils/objectUtils";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import DetailsSectionRenderer from "../../../components/DetailsSectionRenderer";
import ListHeader from "../../../components/ListHeader";
import { usePermissions } from "../../../hooks/usePermissions";
import { faEye, faTrash } from "@fortawesome/free-solid-svg-icons";
import useAction from "../../../hooks/useAction";
import ConfirmationModal from "../../../components/modals/ConfirmationModal";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useGenericFilter from "../../../hooks/filters/useGenericFilter";
import useFilterHandlers from "../../../hooks/useFilterHandlers";
import useUrlFilters from "../../../hooks/useUrlFilters";
import { transformFiltersForUrl } from "../../../utils/transformFiltersForUrl";
import TableWithInactiveFilter from "../../../components/table/TableWithInactiveFilter";
import { Box } from "@mui/material";
import {
  ShipmentItemStatus,
  ShipmentItemStatusEnumById,
} from "../../../enums/shipmentItemStatus";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";
import { shipmentItemStatusColorMap } from "../../../utils/statusColorMap";
import { ShipmentStatusEnum } from "../../../enums/shipment-status-enum";

const DetailsShipmentsPage = () => {
  const { movementId, shipmentId } = useParams();
  const { canAccess } = usePermissions();
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoader();
  const { formData, formatData } = useForm(
    setDefaultFieldValues(detailsMovementFields),
  );
  const queryClient = useQueryClient();

  const {
    get: baseServiceGet,
    getByColumn: baseServiceGetByColumn,
    del: baseServiceDelete,
  } = useBaseService(navigate);

  const {
    openModalConfirmation,
    action,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
  } = useAction(navigate);

  // --- Query for Shipment Details ---
  const { data: shipmentDetails } = useQueryWithLoader({
    queryKey: ["shipmentDetails", shipmentId],
    queryFn: async () => {
      const response = await baseServiceGetByColumn(
        entities.shipments.getById(shipmentId),
      );
      return response.result;
    },
    enabled: !!shipmentId,
  });

  // --- Mutation for Deleting a Shipment Item ---
  const deleteItemFromShipmentMutation = useMutation({
    mutationFn: async (itemId) => {
      showLoader();
      const response = await baseServiceDelete(
        entities.shipments.shipmentItemDelete(shipmentId, itemId),
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        hideLoader();
        handleCancelConfirmation();

        queryClient.invalidateQueries({ queryKey: ["shipmentItemEquipments"] });
        // Invalida ShipmentsPage
        queryClient.invalidateQueries({ queryKey: ["shipments"] });
        // Invalida MovementsShipmentsPage
        queryClient.invalidateQueries({ queryKey: ["movementShipments"] });

        // Invalida MovementPage
        queryClient.invalidateQueries({ queryKey: ["movements"] });
        // Invalida DetailsMovementPage
        queryClient.invalidateQueries({ queryKey: ["movementItems"] });
        // Invalida DetailsShipmentsPage
        queryClient.invalidateQueries({ queryKey: ["shipmentDetails"] });

        if (response.result?.length === 0) {
          navigate(-1, { state: { type: "success" } });
        }
      }
    },
    onError: (err) => {
      console.error("onError ", err);
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const deleteVolumeFromShipmentMutation = useMutation({
    mutationFn: async (itemId) => {
      showLoader();
      const response = await baseServiceDelete(
        entities.shipments.volumes.delete(shipmentId, itemId),
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        hideLoader();
        handleCancelConfirmation();

        queryClient.invalidateQueries({ queryKey: ["shipmentItemEquipments"] });
        // Invalida ShipmentsPage
        queryClient.invalidateQueries({ queryKey: ["shipments"] });
        // Invalida MovementsShipmentsPage
        queryClient.invalidateQueries({ queryKey: ["movementShipments"] });

        // Invalida MovementPage
        queryClient.invalidateQueries({ queryKey: ["movements"] });
        // Invalida DetailsMovementPage
        queryClient.invalidateQueries({ queryKey: ["movementItems"] });
        // Invalida DetailsShipmentsPage
        queryClient.invalidateQueries({ queryKey: ["shipmentDetails"] });

        queryClient.invalidateQueries({ queryKey: ["shipmentItemVolumes"] });

        if (response.result?.length === 0) {
          navigate(-1, { state: { type: "success" } });
        }
      }
    },
    onError: (err) => {
      console.error("onError ", err);
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  // --- Mutation for Activating a Shipment Item ---
  const activateShipmentItemMutation = deleteVolumeFromShipmentMutation;

  const {
    filters: equipmentsFilters,
    setFilters: applyUrlFiltersEquipments,
    pagination: equipmentsPagination,
    changePage: changePageEquipments,
    setPagination: setPaginationEquipments,
  } = useUrlFilters(
    {
      deleted_at: false,
    },
    "shipmentItemEquipments",
  );

  const {
    handleFilterApply: handleFilterApplyEquipments,
    handleFilterClear: handleFilterClearEquipments,
  } = useFilterHandlers(applyUrlFiltersEquipments);

  const { localFilterValues: localFilterValuesEquipments } = useGenericFilter(
    {
      deleted_at: false,
    },
    handleFilterApplyEquipments,
    handleFilterClearEquipments,
  );

  // Buscar todos os equipamentos
  const { data: equipmentsData } = useQueryWithLoader({
    queryKey: [
      "shipmentItemEquipments",
      shipmentId,
      equipmentsFilters,
      equipmentsPagination,
    ],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(equipmentsFilters);
      const params = {
        filters: filtersTransformed,
        page: equipmentsPagination.page,
        perPage: equipmentsPagination.perPage,
      };
      const response = await baseServiceGet(
        entities.shipments.equipments.get(shipmentId),
        params,
      );
      return response.result;
    },
    select: (data) => ({
      equipments: data.data.map((item) => ({
        id: item.id ?? "-",
        movement_id: item?.movement_item?.movement_id ?? "-",
        movement_number: item?.movement_item?.movement?.number ?? "-",
        movement_type:
          item?.movement_item?.movement?.movement_type?.name ?? "-",
        name: `${item?.movement_item?.equipment?.number} - ${item?.movement_item?.equipment?.name}`,
        contract_item:
          item?.movement_item?.order_item?.contract_item?.description ?? "-",
        address:
          item?.address?.formatted_address ??
          item?.movement_item?.equipment?.current_address?.formatted_address ??
          item?.movement_item?.equipment?.address?.formatted_address ??
          "-",
        location:
          item?.movement_item?.order_item?.location?.formatted_location ?? "-",
        details: item?.details ?? "-",
        status: ShipmentItemStatusEnumById[item?.status_id] ?? "-",
        status_id: item?.status_id ?? "-",
        delivery_date: item?.delivery_date ?? "-",
        isDeleted: !!item.deleted_at ?? "-",
      })),
      totalPages: data.last_page,
    }),
  });

  const columnsEquipments = [
    { key: "movement_number", label: "N° Protocolo", width: "12%" },
    { key: "movement_type", label: "Tipo" },
    { key: "name", label: "Equipamento", width: "30%" },
    { key: "contract_item", label: "Item Contrato" },
    { key: "address", label: "Endereço" },
    // { key: "details", label: "Detalhes" },
    { key: "status", label: "Status" },
    { key: "delivery_date", label: "Data Entrega" },
  ];

  const { equipments = [], totalPages: totalPagesEquipments = 1 } =
    equipmentsData || {};

  const {
    filters: filtersVolumes,
    setFilters: applyUrlFiltersVolumes,
    pagination: paginationVolumes,
    changePage: changePageVolumes,
    setPagination: setPaginationVolumes,
  } = useUrlFilters({}, `movementVolumes_${movementId}`);

  const {
    handleFilterApply: handleFilterApplyVolumes,
    handleFilterClear: handleFilterClearVolumes,
  } = useFilterHandlers(applyUrlFiltersVolumes);

  const { localFilterValues: localFilterValuesVolumes } = useGenericFilter(
    {},
    handleFilterApplyVolumes,
    handleFilterClearVolumes,
  );

  const {
    data: volumesData,
    isLoading: isLoadingVolumes,
    isFetching: isFetchingVolumes,
  } = useQueryWithLoader({
    queryKey: [
      "shipmentItemVolumes",
      shipmentId,
      filtersVolumes,
      paginationVolumes,
    ],
    queryFn: async () => {
      const params = {
        filters: {},
        page: paginationVolumes.page,
        perPage: paginationVolumes.perPage,
      };

      const response = await baseServiceGet(
        entities.shipments.volumes.get(shipmentId),
        params,
      );
      return response.result;
    },
    staleTime: 60 * 1000, // 60 segundos de cache
    keepPreviousData: true, // Mantém dados anteriores enquanto busca novos
    select: (data) => {
      return {
        shipmentsVolumes: data?.data?.map((item) => {
          const volumeItems = item?.items ?? [];

          const items = volumeItems.map((i) => {
            const isEquipment = i.equipment_id !== null;

            return {
              type: isEquipment ? "equipment" : "product",

              name: isEquipment
                ? `${i.equipment?.number} - ${i.equipment?.name}`
                : `${i.product_item?.product?.name} | ${i.product_item?.category?.name} | ${i.product_item?.condition?.name}`,

              address:
                i.movement_item?.order_item?.address?.formatted_address ?? "-",

              location:
                i.movement_item?.order_item?.location?.formatted_location ??
                "-",

              quantity: i.quantity ?? "-",
            };
          });

          return {
            // Volume
            id: item.id ?? "-",
            code: item.code ?? "-",
            name: item.name ?? "-",
            status:
              ShipmentItemStatusEnumById[item?.shipment?.status_id] ?? "-",
            status_id: item?.shipment?.status_id ?? "-",

            // Movement
            movement_number: item?.movement?.number ?? "-",
            movement_type: item?.movement?.movement_type?.name ?? "-",

            // Items unificado
            items,

            isDeleted: !!item.deleted_at,
            delivery_date: item?.delivery_date ?? "-",
            created_at: item?.created_at ?? "-",
          };
        }),
        totalPages: data.last_page,
      };
    },
  });

  const { shipmentsVolumes = [], totalPagesVolumes = 1 } = volumesData || {};

  const volumesColumns = [
    { key: "movement_number", label: "N° Protocolo" },
    { key: "movement_type", label: "Tipo" },
    { key: "name", label: "Volume" },
    { key: "code", label: "Código" },
    { key: "status", label: "Status" },
    { key: "delivery_date", label: "Confirmado Em", align: "left" },
  ];

  const volumesCollapseConfig = {
    key: ["items"],
    totalField: [],
    columns: [
      [
        { key: "items.name", label: "Item" },
        { key: "items.address", label: "Endereço" },
        { key: "items.quantity", label: "Quantidade", numeric: true },
      ],
    ],
  };

  // UseEffect para formatar os dados e preencher o formulário de detalhes do carregamento
  useEffect(() => {
    if (shipmentDetails) {
      formatData(
        {
          ...shipmentDetails,
          created_by: shipmentDetails.user?.name,
          status_name: shipmentDetails.status.name,
          deleted_at: shipmentDetails.deleted_at ? "Inativo" : "Ativo",
        },
        detailsMovementFields,
      );
    }
  }, [shipmentDetails, formatData]);

  // const actionsShipmentItem = useMemo(
  //   () => [
  //     {
  //       id: "delete",
  //       icon: faTrash,
  //       title: "Remover Item",
  //       buttonClass: "btn-danger",
  //       permission: "Excluir itens de carregamentos",
  //       onClick: (item) => {
  //         showConfirmation(
  //           item,
  //           `Você tem certeza que deseja remover o item desse carregamento?`,
  //           async () => {
  //             try {
  //               await deleteItemFromShipmentMutation.mutateAsync(item.id);
  //             } catch (error) {
  //               console.error("Erro ao excluir item do carregamento:", {
  //                 status: error.status,
  //                 message: error.message,
  //               });
  //             }
  //           },
  //         );
  //       },
  //       show: (item) =>
  //         item.status_id === ShipmentItemStatus.AGUARDANDO_CONFIRMACAO &&
  //         canAccess("Excluir itens de carregamentos"),
  //     },
  //   ],
  //   [canAccess, showConfirmation, deleteItemFromShipmentMutation],
  // );

  const actionsVolume = useMemo(
    () => [
      {
        id: "delete",
        icon: faTrash,
        title: "Remover Volume",
        buttonClass: "btn-danger",
        permission: "Excluir itens de carregamentos",
        onClick: (volume) => {
          showConfirmation(
            volume,
            `Você tem certeza que deseja remover o volume desse carregamento?`,
            async () => {
              try {
                await deleteVolumeFromShipmentMutation.mutateAsync(volume.id);
              } catch (error) {
                console.error("Erro ao excluir volume do carregamento:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          );
        },
        show: (volume) =>
          volume.status_id === ShipmentItemStatus.AGUARDANDO_CONFIRMACAO &&
          canAccess("Excluir itens de carregamentos"),
      },
    ],
    [canAccess, showConfirmation, deleteVolumeFromShipmentMutation],
  );

  const [buttonHidden, setButtonHidden] = useState(false);

  useEffect(() => {
    setButtonHidden(shipmentDetails?.status_id === ShipmentStatusEnum.PENDING);
  }, [shipmentDetails]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title={`Detalhes do Carregamento`}
        showBackButton={true}
        backUrl={`/aluguel/movimentos/${movementId}/carregamentos`}
      />
      <div className="container-fluid p-1">
        <DetailsSectionRenderer
          formData={formData}
          sections={detailsMovementFields}
          readOnly
        />

        <div>
          <ListHeader
            title="Equipamentos"
            buttonIsNotVisible={true}
            canAccess={canAccess}
          />
          <TableWithInactiveFilter
            data={equipments}
            columns={columnsEquipments}
            // actions={actionsShipmentItem}
            totalPages={totalPagesEquipments}
            currentPage={equipmentsPagination.page - 1}
            onPageChange={changePageEquipments}
            rowsPerPage={equipmentsPagination.perPage}
            onRowsPerPageChange={(event) => {
              const newPerPage = +event.target.value;
              setPaginationEquipments({ page: 1, perPage: newPerPage });
            }}
            filterValues={localFilterValuesEquipments}
            localFilterValues={localFilterValuesEquipments}
            applyUrlFilters={applyUrlFiltersEquipments}
            type="default"
            rowColorConfig={{
              colorMap: shipmentItemStatusColorMap,
              propertyKey: "status",
            }}
          />
        </div>

        <Box>
          <ListHeader
            title="Volumes"
            canAccess={canAccess}
            buttonText={"Adicionar volumes"}
            permission={"Criar itens de carregamentos"}
            buttonLink={`/aluguel/carregamentos/validar?shipment_id=${shipmentId}`} // * Rota utilizada para adicionar volumes ao carregamento
            buttonIsNotVisible={!buttonHidden}
          />
          <TableWithInactiveFilter
            data={shipmentsVolumes}
            columns={volumesColumns}
            actions={actionsVolume}
            totalPages={totalPagesVolumes}
            currentPage={paginationVolumes.page - 1}
            onPageChange={changePageVolumes}
            rowsPerPage={paginationVolumes.perPage}
            onRowsPerPageChange={(event) => {
              const newPerPage = +event.target.value;
              setPaginationVolumes({ page: 1, perPage: newPerPage });
            }}
            filterValues={localFilterValuesVolumes}
            localFilterValues={localFilterValuesVolumes}
            applyUrlFilters={applyUrlFiltersVolumes}
            collapseConfig={volumesCollapseConfig}
            showFilterInactive={false}
            showActions={true}
            rowColorConfig={{
              colorMap: shipmentItemStatusColorMap,
              propertyKey: "status",
            }}
            isLoading={isLoadingVolumes}
            isFetching={isFetchingVolumes}
          />
        </Box>

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={selectedItem ? `${selectedItem.id}` : ""}
          text={action.text}
          isConfirming={
            deleteVolumeFromShipmentMutation.isPending ||
            activateShipmentItemMutation.isPending
          }
        />
      </div>
    </MainLayout>
  );
};

export default DetailsShipmentsPage;
